// src/components/features/content/ContentForm.jsx
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  Link as ReferenceIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  AutoAwesome as MagicIcon
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Import custom components
import { Card, MagicLoader } from '../../../design-system';

// Import form standardized components
import { FormStepperEnhanced, FormStep } from '../../shared/forms';
import { FormPageHeader } from '../../ui/FormFields';

// Import form sections
import BasicInfoStep from './form-steps/BasicInfoStep';
import BasicInfoStepFallback from './form-steps/BasicInfoStepFallback';
import AssetsStep from './form-steps/AssetsStep';
import ReferencesStep from './form-steps/ReferencesStep';
import DistributionStep from './form-steps/DistributionStep';
import MagicalElementsForm from '../../ui/MagicalElementsForm';

// Import services and stores
import useContentStore from '../../../stores/contentStore';
import useBrandStore from '../../../stores/brandStore';
import useCampaignStore from '../../../stores/campaignStore';
import useNotificationStore from '../../../stores/notificationStore';

// Import utils and data
import { initialContentData } from './form-utils/contentFormData';
import contentService from '../../../services/contentService';

// ContentForm component
const ContentForm = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse query params
  const queryParams = new URLSearchParams(location.search);
  const brandIdParam = queryParams.get('brandId');
  const campaignIdParam = queryParams.get('campaignId');
  
  // Is this an edit form or create form
  const isEditMode = Boolean(id);
  
  // Store hooks
  const { 
    currentContent,
    isLoading, 
    error, 
    fetchContentById,
    createContent,
    updateContent,
    clearCurrentContent,
    clearError
  } = useContentStore();
  
  const { brands, fetchBrands } = useBrandStore();
  const { campaigns, fetchCampaigns, fetchBrandCampaigns } = useCampaignStore();
  const { showSuccess, showError } = useNotificationStore();
  
  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [contentData, setContentData] = useState({...initialContentData});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tempAssets, setTempAssets] = useState([]);
  const [tempReferences, setTempReferences] = useState([]);
  const [uploadingAssets, setUploadingAssets] = useState(false);
  const [uploadingReferences, setUploadingReferences] = useState(false);
  
  // Load data on component mount
  useEffect(() => {
    loadBrands();
    
    if (isEditMode) {
      loadContent();
    } else {
      updateCategoryOptions(initialContentData.type);
      
      // If not edit mode, set initial values from URL params
      if (brandIdParam) {
        setContentData(prev => ({
          ...prev,
          brandId: brandIdParam
        }));
        loadCampaigns(brandIdParam);
      }
      
      if (campaignIdParam) {
        setContentData(prev => ({
          ...prev,
          campaignId: campaignIdParam
        }));
      }
    }
    
    // Cleanup on unmount
    return () => {
      clearCurrentContent();
      clearError();
    };
  }, []);
  
  // Update form data when currentContent changes in edit mode
  useEffect(() => {
    if (isEditMode && currentContent) {
      // Clone the current content
      const contentDataCopy = { ...currentContent };
      
      // Handle brandId and campaignId which might be objects or IDs
      if (typeof contentDataCopy.brandId === 'object') {
        contentDataCopy.brandId = contentDataCopy.brandId._id;
      }
      
      if (typeof contentDataCopy.campaignId === 'object') {
        contentDataCopy.campaignId = contentDataCopy.campaignId._id;
      }
      
      // Load campaigns for the brand
      if (contentDataCopy.brandId) {
        loadCampaigns(contentDataCopy.brandId);
      }
      
      // Set form values
      setContentData(contentDataCopy);
      
      // Load categories based on content type
      updateCategoryOptions(contentDataCopy.type);
    }
  }, [currentContent]);
  
  // Load brands
  const loadBrands = async () => {
    try {
      await fetchBrands({ limit: 100 });
    } catch (error) {
      console.error('Failed to load brands:', error);
    }
  };
  
  // Load content in edit mode
  const loadContent = async () => {
    try {
      await fetchContentById(id);
    } catch (error) {
      showError('Failed to load content: ' + error.message);
    }
  };
  
  // Load campaigns for selected brand
  const loadCampaigns = async (brandId) => {
    if (!brandId) return;
    
    try {
      // Fetch campaigns from API
      await fetchBrandCampaigns(brandId, { limit: 100 });
      
      // Force a refresh of the form state after campaigns are loaded
      // by setting contentData with the same values, triggering render cycle
      setTimeout(() => {
        setContentData(prev => ({...prev}));
      }, 100);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };
  
  // Update category options based on selected content type
  const updateCategoryOptions = (contentType) => {
    let options = [];
    
    // Different categories based on content type
    switch (contentType) {
      case 'post':
      case 'carousel':
        options = ['Product', 'Lifestyle', 'Promotional', 'Educational', 'Engagement', 'Brand Story', 'UGC', 'Behind the Scenes'];
        break;
      case 'story':
      case 'reel':
        options = ['Quick Update', 'Feature Highlight', 'Day in the Life', 'Tutorial', 'Testimonial', 'Trend', 'Q&A'];
        break;
      case 'video':
        options = ['Brand Video', 'Product Demo', 'Tutorial', 'Customer Story', 'Interview', 'Event Recap'];
        break;
      case 'article':
        options = ['Blog Post', 'Case Study', 'News', 'Press Release', 'Thought Leadership'];
        break;
      case 'announcement':
        options = ['Product Launch', 'Company News', 'Feature Update', 'Important Notice'];
        break;
      case 'promotion':
        options = ['Sale', 'Discount', 'Special Offer', 'Seasonal Promotion', 'Event Promotion'];
        break;
      case 'event':
        options = ['Webinar', 'Workshop', 'Conference', 'Launch Event', 'Meetup'];
        break;
      default:
        options = ['General', 'Promotional', 'Educational', 'Engagement'];
    }
    
    setCategoryOptions(options);
    
    // Clear category if it doesn't exist in the new options
    const currentCategory = contentData.category;
    if (currentCategory && !options.includes(currentCategory)) {
      setContentData(prev => ({
        ...prev,
        category: ''
      }));
    }
  };
  
  // Handle step change
  const handleStepChange = (newStep) => {
    setActiveStep(newStep);
    window.scrollTo(0, 0);
  };
  
  // Watch for brand ID changes to reload campaigns
  useEffect(() => {
    if (contentData.brandId) {
      loadCampaigns(contentData.brandId);
    }
  }, [contentData.brandId]);
  
  // Handle form field changes
  const handleChange = (name, value) => {
    // Special handling for content type
    if (name === 'type') {
      updateCategoryOptions(value);
    }
    
    // Handle brand changes
    if (name === 'brandId') {
      // Clear campaignId when brand changes
      const updatedData = {
        ...contentData,
        [name]: value,
        campaignId: ''
      };
      
      // Update form state
      setContentData(updatedData);
      
      // Load campaigns for the selected brand
      if (value) {
        loadCampaigns(value);
      }
      
      // Clear validation error if field now has a valid value
      if (validationErrors[name] && value) {
        const updatedErrors = {...validationErrors};
        delete updatedErrors[name];
        setValidationErrors(updatedErrors);
      }
    } else {
      // Update form state for all other fields
      const updatedData = {
        ...contentData,
        [name]: value
      };
      setContentData(updatedData);
      
      // Clear validation error if field now has a valid value
      if (validationErrors[name] && value) {
        const updatedErrors = {...validationErrors};
        delete updatedErrors[name];
        setValidationErrors(updatedErrors);
      }
    }
  };
  
  // Handle nested field changes
  const handleNestedChange = (parent, name, value) => {
    const updatedData = {
      ...contentData,
      [parent]: {
        ...contentData[parent],
        [name]: value
      }
    };
    
    setContentData(updatedData);
    
    // Clear validation error if field now has a valid value
    if (validationErrors[`${parent}.${name}`]) {
      if (value) {
        // If the field now has a value, clear its validation error
        const updatedErrors = {...validationErrors};
        delete updatedErrors[`${parent}.${name}`];
        setValidationErrors(updatedErrors);
      }
    }
  };
  
  // State for validation errors and touched fields
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  // Step validation function
  const validateStep = async (stepIndex) => {
    // Clear previous errors for this step only
    const currentErrors = {...validationErrors};
    Object.keys(currentErrors).forEach(key => {
      if (key === 'title' || key === 'type' || key === 'category' || key === 'brandId') {
        if (stepIndex === 0) delete currentErrors[key];
      } else if (key === 'scheduledDate') {
        if (stepIndex === 3) delete currentErrors[key];
      }
    });
    
    // Start with cleared errors specific to this step
    setValidationErrors(currentErrors);
    
    // Create a new errors object for this step's validation
    const errors = {};
    let isValid = true;
    
    // Create a temporary object to track fields that need to be marked as touched
    // if validation fails
    const fieldsToTouch = {};
    
    console.log('Validating step', stepIndex, 'with data:', contentData);
    
    // Validate based on step index
    switch (stepIndex) {
      case 0: // Basic Information
        // Validate required fields
        if (!contentData.title || contentData.title.trim() === '') {
          errors.title = 'Content title is required';
          fieldsToTouch.title = true;
          isValid = false;
        }
        
        if (!contentData.type) {
          errors.type = 'Content type is required';
          fieldsToTouch.type = true;
          isValid = false;
        }
        
        if (!contentData.category) {
          errors.category = 'Category is required';
          fieldsToTouch.category = true;
          isValid = false;
        }
        
        if (!contentData.brandId) {
          errors.brandId = 'Brand is required';
          fieldsToTouch.brandId = true;
          isValid = false;
        }
        
        console.log('Basic info validation results:', { isValid, errors });
        break;
        
      case 1: // Assets
        // Optional validation for assets if needed
        break;
        
      case 2: // References
        // Optional validation for references if needed
        break;
        
      case 3: // Distribution Settings
        // Validate distribution date if scheduling
        if (contentData.schedule && contentData.scheduleType === 'specific' && !contentData.scheduledDate) {
          errors.scheduledDate = 'Scheduled date is required';
          fieldsToTouch.scheduledDate = true;
          isValid = false;
        }
        break;
        
      default:
        break;
    }
    
    // Update validation errors state
    if (Object.keys(errors).length > 0) {
      setValidationErrors(prev => ({
        ...prev,
        ...errors
      }));
    }
    
    // Only mark fields as touched if validation fails
    if (!isValid) {
      // Update touched fields state with the fields that failed validation
      await new Promise(resolve => {
        setTouchedFields(prev => {
          const result = {
            ...prev,
            [stepIndex]: {
              ...(prev[stepIndex] || {}),
              ...fieldsToTouch
            }
          };
          setTimeout(resolve, 10);
          return result;
        });
      });
      
      console.log(`Validation failed for step ${stepIndex}:`, errors);
    }
    
    return isValid;
  };
  
  // Function to get errors for a specific step
  const getErrorsForStep = (stepIndex) => {
    switch (stepIndex) {
      case 0: // Basic Information
        return {
          title: validationErrors.title,
          type: validationErrors.type,
          category: validationErrors.category,
          brandId: validationErrors.brandId
        };
      case 1: // Assets
        return {};
      case 2: // References
        return {};
      case 3: // Distribution Settings
        return {
          scheduledDate: validationErrors.scheduledDate
        };
      default:
        return {};
    }
  };
  
  // Function to get touched fields for a step
  const getTouchedForStep = (stepIndex) => {
    return touchedFields[stepIndex] || {};
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let contentId;
      let result;
      
      // Clone values to prevent issues with assets and references
      const contentDataCopy = { ...contentData };
      delete contentDataCopy.assets;
      delete contentDataCopy.references;
      
      // Create or update content
      if (isEditMode) {
        result = await updateContent(id, contentDataCopy);
        contentId = id;
        showSuccess('Content updated successfully');
      } else {
        result = await createContent(contentDataCopy);
        contentId = result._id;
        showSuccess('Content created successfully');
      }
      
      // Upload temporary assets if any
      if (tempAssets.length > 0) {
        setUploadingAssets(true);
        try {
          console.log(`Starting upload of ${tempAssets.length} assets`);
          
          // Process assets one by one to avoid any boundary issues
          for (let i = 0; i < tempAssets.length; i++) {
            const asset = tempAssets[i];
            if (!asset.file) continue;
            
            console.log(`Uploading asset ${i + 1}/${tempAssets.length}: ${asset.file.name}`);
            
            // Prepare metadata (convert everything to strings)
            const metadata = {
              type: asset.type || 'image',
              caption: asset.caption || asset.originalname || asset.file.name,
              altText: asset.altText || '',
              order: i.toString() // Ensure order is a string
            };
            
            // Upload the asset individually
            await contentService.uploadContentAsset(contentId, asset.file, metadata);
            
            // Small delay between uploads to prevent overwhelming the server
            if (i < tempAssets.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          }
          
          showSuccess('Assets uploaded successfully');
        } catch (error) {
          console.error('Asset upload error:', error);
          showError(`Failed to upload assets: ${error.message || 'Unknown error'}`);
        } finally {
          setUploadingAssets(false);
        }
      }
      
      // Upload temporary references if any
      if (tempReferences.length > 0) {
        setUploadingReferences(true);
        try {
          console.log(`Starting upload of ${tempReferences.length} references`);
          
          // Process references one by one
          for (let i = 0; i < tempReferences.length; i++) {
            const reference = tempReferences[i];
            
            if (reference.file) {
              // Create a new FormData for each reference
              const formData = new FormData();
              formData.append('file', reference.file);
              formData.append('type', reference.type || 'image');
              formData.append('caption', reference.caption || reference.file.name);
              formData.append('altText', reference.altText || '');
              
              await contentService.addContentReference(contentId, formData);
            } else if (reference.url) {
              // For URL references, just send the data object
              await contentService.addContentReference(contentId, {
                type: reference.type || 'link',
                url: reference.url,
                caption: reference.caption || '',
                altText: reference.altText || ''
              });
            }
            
            // Small delay between uploads
            if (i < tempReferences.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          }
          
          showSuccess('References added successfully');
        } catch (error) {
          console.error('Reference upload error:', error);
          showError(`Failed to add references: ${error.message || 'Unknown error'}`);
        } finally {
          setUploadingReferences(false);
        }
      }
      
      // Navigate to content detail
      navigate(`/content/${contentId}`);
    } catch (error) {
      showError(error.message || 'Failed to save content');
      setIsSubmitting(false);
    }
  };
  
  // Loading state
  if ((isLoading && isEditMode && !currentContent)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoader variant="mystical" size="large" message={isEditMode ? "Loading content details..." : "Preparing content form..."} />
      </Box>
    );
  }
  
  // Error state
  if (error && isEditMode) {
    return (
      <Box sx={{ py: 3 }}>
        <Card
          elevation={0}
          sx={{ 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/content')}
          >
            Back to Content
          </Button>
        </Card>
      </Box>
    );
  }
  
  // Define steps for FormStepperEnhanced
  const formSteps = [
    {
      label: 'Basic Information',
      icon: <DescriptionIcon />,
      optional: false,
      description: 'Enter content details',
      component: (
        <FormStep
          label="Basic Information"
          description="Enter content details"
          icon={<DescriptionIcon />}
        >
          <BasicInfoStepFallback
            formik={{
              values: contentData,
              setFieldValue: (name, value) => handleChange(name, value),
              handleChange: (e) => handleChange(e.target.name, e.target.value),
              handleBlur: () => {}, // Not needed with our validation approach
              touched: getTouchedForStep(0),
              errors: getErrorsForStep(0)
            }}
            brands={brands}
            campaigns={campaigns}
            categoryOptions={categoryOptions}
            onContentTypeChange={(newType) => updateCategoryOptions(newType)}
            onBrandChange={(brandId) => {
              // Direct value handling in fallback version
              if (brandId) {
                // Use setState directly to guarantee update
                setContentData(prev => ({
                  ...prev,
                  brandId: brandId,
                  campaignId: ''
                }));
                loadCampaigns(brandId);
                
                // Also clear any validation errors
                if (validationErrors.brandId) {
                  setValidationErrors(prev => {
                    const newErrors = {...prev};
                    delete newErrors.brandId;
                    return newErrors;
                  });
                }
              }
            }}
            isEditMode={isEditMode}
          />
        </FormStep>
      )
    },
    {
      label: 'Assets & Media',
      icon: <ImageIcon />,
      optional: false,
      description: 'Add images, videos and assets',
      component: (
        <FormStep
          label="Assets & Media"
          description="Add images, videos and assets"
          icon={<ImageIcon />}
        >
          <AssetsStep
            formik={{
              values: contentData,
              setFieldValue: (name, value) => handleChange(name, value)
            }}
            tempAssets={tempAssets}
            setTempAssets={setTempAssets}
          />
        </FormStep>
      )
    },
    {
      label: 'References',
      icon: <ReferenceIcon />,
      optional: true,
      description: 'Add references and sources',
      component: (
        <FormStep
          label="References"
          description="Add references and sources"
          icon={<ReferenceIcon />}
        >
          <ReferencesStep
            formik={{
              values: {
                references: tempReferences
              },
              setFieldValue: (name, value) => {
                if (name === 'references') {
                  setTempReferences(value);
                }
              }
            }}
            tempReferences={tempReferences}
            setTempReferences={setTempReferences}
            isEditMode={isEditMode}
          />
        </FormStep>
      )
    },
    {
      label: 'Distribution Settings',
      icon: <ScheduleIcon />,
      optional: false,
      description: 'Configure publishing settings',
      component: (
        <FormStep
          label="Distribution Settings"
          description="Configure publishing settings"
          icon={<ScheduleIcon />}
        >
          <DistributionStep
            formik={{
              values: contentData,
              setFieldValue: (name, value) => handleChange(name, value),
              handleChange: (e) => handleChange(e.target.name, e.target.value),
              touched: getTouchedForStep(3),
              errors: getErrorsForStep(3)
            }}
          />
        </FormStep>
      )
    }
  ];
  
  // Define breadcrumbs for FormPageHeader
  const breadcrumbs = [
    { label: 'Content', path: '/content' },
    { label: isEditMode ? `Edit ${currentContent?.title || 'Content'}` : 'Create Content' }
  ];
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Page Header with Breadcrumbs */}
      <FormPageHeader
        title={isEditMode ? `Edit ${currentContent?.title || 'Content'}` : 'Create New Content'}
        subtitle={isEditMode 
          ? 'Update your content details, assets, and distribution settings' 
          : 'Create and configure new content for your brand'}
        breadcrumbs={breadcrumbs}
        entityType="Content"
        isEditMode={isEditMode}
      />
      
      {/* Enhanced Form Stepper */}
      <FormStepperEnhanced
        steps={formSteps}
        activeStep={activeStep}
        onStepChange={handleStepChange}
        onSubmit={handleSubmit}
        validateStep={validateStep}
        submitButtonText={isEditMode ? 'Save Changes' : 'Create Content'}
        submitButtonIcon={<SaveIcon />}
        submitting={isSubmitting || uploadingAssets || uploadingReferences}
        cancelLink="/content"
        magical={true}
        nextButtonText="Next"
        backButtonText="Back"
        formState={contentData}
        setFormState={setContentData}
        errors={validationErrors}
      />
    </Box>
  );
};

export default ContentForm;